/**
 * InfoSheet
 *
 * An info sheet with a single Ok button and closeOnClickOutside enabled.
 * Use this instead of ConfirmSheet when no confirmation action is needed.
 */

import type { l10n } from '@/presentation/locale/l10n';
import { SheetService } from '../service/SheetService';
import { InfoContent } from './components/InfoContent';

/**
 * Configuration for showing an info sheet
 */
export interface InfoSheetConfig {
  /** Title displayed at the top of the sheet */
  title: string;

  /** Info text/message displayed in the sheet body */
  infoText: string;

  /** Text for the ok button (default: 'Ok') */
  okText?: string;

  /** Optional container element, screen key string, or null */
  container?: HTMLElement | string | null;

  /** Optional screen key of the route opening this sheet */
  screenKey?: string;

  /** Force the sheet to render as a dialog instead of a bottom sheet */
  forceDialog?: boolean;

  /** Optional custom max width for the dialog (e.g., '75vw', '400px') */
  maxWidth?: string;

  /**
   * Optional stable QA id for THIS info sheet's content root. When provided,
   * `InfoContent` renders it as a `data-qa-id` on its root element so a
   * UI-test plan can locate this specific info sheet (e.g.
   * `data-qa-id="media-info-sheet"`). Test-only; never affects rendering.
   *
   * Distinct from the surface-level `sheet-info` wrapper id every info sheet
   * shares — that wrapper is not unique per-call, so callers that need to
   * assert on a particular info sheet pass their own `qaId` here.
   */
  qaId?: string;
}

/**
 * Show an info sheet with a single Ok button
 *
 * @param config - Configuration for the info sheet
 * @returns Promise that resolves when the sheet is dismissed
 *
 * @example
 * await InfoSheet.show({
 *   title: 'Subscription Info',
 *   infoText: 'This subscription renews automatically.',
 *   screenKey,
 * });
 */
const show = (config: InfoSheetConfig): Promise<void> => {
  return new Promise((resolve) => {
    SheetService.show({
      content: <InfoContent config={config} onClose={resolve} />,
      onClose: resolve,
      closeOnClickOutside: true,
      container: config.container,
      screenKey: config.screenKey,
      forceDialog: config.forceDialog,
      maxWidth: config.maxWidth,
      qaId: 'info',
    });
  });
};

/**
 * Configuration for the "Not implemented yet" info sheet shortcut.
 */
export interface ShowNotImplementedYetConfig {
  /** Locale bundle from `useLocale()` — supplies the title/body strings */
  l10n: l10n;

  /** Optional screen key of the route opening this sheet */
  screenKey?: string;
}

/**
 * Show the canonical "Not implemented yet" info sheet.
 *
 * Wraps `InfoSheet.show(...)` with the shared
 * `l10n.common.notImplementedYetTitle` / `l10n.common.notImplementedYetMessage`
 * pair so callers don't duplicate the same three lines at every placeholder
 * tap target.
 *
 * @example
 * InfoSheet.showNotImplementedYet({ l10n, screenKey });
 */
const showNotImplementedYet = (
  config: ShowNotImplementedYetConfig,
): Promise<void> =>
  show({
    title: config.l10n.common.notImplementedYetTitle,
    infoText: config.l10n.common.notImplementedYetMessage,
    screenKey: config.screenKey,
  });

export const InfoSheet = {
  show,
  showNotImplementedYet,
};
